import * as React from 'react';

import { Button, Modal, Stack } from 'react-bootstrap';
import {
    DateTypesEnum,
    ExportOptions,
    QueryToolDefaultColumnList,
} from '../../utilities/Constants';
import {
    ICustomReportOption,
    IReportOptions,
    IReportParametersModel,
    IReportTypeModel,
} from '../../interfaces/Report/IReport';
import { IQueryColumnDefinitionModel, IQueryFilterCriteria } from '../../interfaces/IQuery';
import { cloneDeep, isEmpty } from 'lodash';

import { ApiRoutes } from '../../utilities/ApiRoutes';
import Common from '../../stores/Common';
import { DateRangePicker } from '../../pages/query/DateRangePicker';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { DocumentTitle } from '../shared/DocumentTitle';
import { ExportFileButton } from '../shared/ExportFileButton';
import { FieldFilter } from './FieldFilter';
import Html5ReportViewer from './Html5ReportViewer';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import { ZoneContextType } from '../../utilities/Constants';
import classNames from 'classnames';

interface IViewExportReportProps {
    data: IReportTypeModel;
    defaultAuditFieldSettings?: IQueryColumnDefinitionModel[];
    isLoading: boolean;
    user: IUserModel;
    typeId?: number;
    zoneContext: ZoneContextType;
}

interface IViewExportReportState {
    addEditComment: boolean;
    initReportParameters: IReportParametersModel;
    pendingResponse: boolean;
    refreshReport: boolean;
    renderReportViewer?: boolean;
    reportParameters: IReportParametersModel;
    reportTypeDetail: IReportTypeModel;
    showSettingsModal: boolean;
    typeValue?: string;
    validation: IValidation;
}

export class ViewExportReport extends React.Component<
    IViewExportReportProps,
    IViewExportReportState
> {
    constructor(props: any) {
        super(props);

        this.state = {
            addEditComment: false,
            initReportParameters: {},
            pendingResponse: false,
            refreshReport: false,
            reportParameters: {},
            reportTypeDetail: {
                id: 0,
            },
            showSettingsModal: false,
            validation: {},
        };
    }

    componentDidMount() {
        const { data, zoneContext } = this.props;

        const getCustomOptionsFromAvailableOptions = (availableOptions: IReportOptions) => {
            const customOptions: ICustomReportOption[] = [];

            if (availableOptions.includeNotes)
                customOptions.push(ExportOptions.IncludeSignificantChanges);
            if (availableOptions.includeTasks) customOptions.push(ExportOptions.IncludeTactics);

            return customOptions;
        };

        const baseOptions = cloneDeep(data?.availableReportOptions ?? {});
        const availableOptions = {
            ...baseOptions,
            customOptions: getCustomOptionsFromAvailableOptions(baseOptions),
        };

        const reportParameters: IReportParametersModel = {
            reportType: { id: this.props.typeId ?? 0 },
            organizationGuid: zoneContext.zoneGuid,
        };

        this.setState(
            {
                reportTypeDetail: { ...data, availableReportOptions: availableOptions },
                pendingResponse: false,
                reportParameters,
                initReportParameters: reportParameters,
            },
            this.autoViewDownloadReport
        );
    }

    handleCommentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const reportParametersCopy = cloneDeep(this.state.reportParameters);
        reportParametersCopy.commentText = event.target.value;
        this.setState({ reportParameters: reportParametersCopy });
    };

    cancelSettingsModal = () => {
        this.setState({
            reportParameters: this.state.initReportParameters,
            showSettingsModal: false,
            validation: {},
            refreshReport: false,
        });
    };

    refreshReport = () => {
        const { reportParameters, validation } = this.state;
        const reportParametersCopy = cloneDeep(reportParameters);
        const validationCopy = cloneDeep(validation);

        const { date } = reportParametersCopy;
        const hasPartialDate = !!date && !!date.startDate !== !!date.endDate;

        if (hasPartialDate) {
            validationCopy.dateRange = [DisplayMessages.BothStartAndEndDateRequired];
            this.setState({ validation: validationCopy });
            return;
        }

        this.setState(
            {
                initReportParameters: reportParametersCopy,
                refreshReport: false,
                showSettingsModal: false,
                renderReportViewer: true,
            },
            () => {
                // toggle so child detects refresh
                this.setState({ refreshReport: true });
            }
        );
    };

    autoViewDownloadReport = () => {
        const { viewInUI, allowComments, availableReportOptions } = this.state.reportTypeDetail;
        const hasOptions = !isEmpty(availableReportOptions);

        if (viewInUI && hasOptions) {
            this.refreshReport();
        } else if (!viewInUI && !allowComments && !hasOptions) {
            this.setState({ showSettingsModal: true });
        }
    };

    handleChangeDate = (event: React.ChangeEvent<HTMLInputElement>, dateType: number) => {
        const { value } = event.target;
        const reportParameters = cloneDeep(this.state.reportParameters);
        reportParameters.date ??= { type: 0 };

        if (dateType === DateTypesEnum.StartDate) {
            reportParameters.date.startDate = value;
        } else if (dateType === DateTypesEnum.EndDate) {
            reportParameters.date.endDate = value;
        }

        this.setState({ reportParameters });
    };

    handleCheckedChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { name, checked } = event.target;
        const reportParameters = cloneDeep(this.state.reportParameters);

        reportParameters.options ??= {};

        switch (name) {
            case 'chkIncludePastProjected':
                reportParameters.options.includePastProjected = checked;
                break;
            case 'chkIncludeNotes':
                reportParameters.options.includeNotes = checked;
                break;
            case 'chkIncludeTasks':
                reportParameters.options.includeTasks = checked;
                break;
            case 'chkSaveReport':
                reportParameters.saveReport = checked;
                break;
        }

        this.setState({ reportParameters });
    }

    handleAuditFieldsChange = (selectedValues: IQueryFilterCriteria[]) => {
        const values: IQueryColumnDefinitionModel[] = selectedValues.map((item) => ({
            name: item.value,
            assertionDescriptorGuid: item.assertionDescriptorGuid,
            staticItemId: item.staticItemId,
        }));

        this.setState((prevState) => {
            const reportParameters = cloneDeep(prevState.reportParameters);
            reportParameters.auditFields = values;

            return { reportParameters };
        });
    };

    handleDisplayFieldsChange = (selectedValues: IQueryFilterCriteria[]) => {
        const values: IQueryColumnDefinitionModel[] = selectedValues.map((item) => ({
            name: item.value,
            assertionDescriptorGuid: item.assertionDescriptorGuid,
            staticItemId: item.staticItemId,
        }));

        this.setState((prevState) => {
            const reportParameters = cloneDeep(prevState.reportParameters);
            reportParameters.displayFields = values;

            return { reportParameters };
        });
    };

    generateAndDownloadReport = async () => {
        const validation: IValidation = {};

        const { reportTypeDetail, reportParameters } = this.state;
        const includeDateRange = reportTypeDetail.availableReportOptions?.includeDateRange;

        const date = reportParameters?.date;
        const hasValidDate =
            (date?.startDate && Common.isValidDate(date.startDate)) ||
            (date?.endDate && Common.isValidDate(date.endDate));

        if (includeDateRange && !hasValidDate) {
            validation.model = ['Enter a valid date range'];
            this.setState({ validation });
            return;
        }

        this.setState({ pendingResponse: true, validation: validation });

        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/' + ApiRoutes.ExportReport, true);
        xhr.responseType = 'blob';
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-Tenant-Guid', this.props.zoneContext.zoneGuid);

        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
                const reportParameters = this.state.reportParameters;
                reportParameters.commentText = '';
                this.setState({
                    pendingResponse: false,
                    reportParameters: reportParameters,
                    addEditComment: false,
                    showSettingsModal: false,
                });

                let fileName = 'Report.pdf';
                const blob = xhr.response;

                const contentDisposition = xhr.getResponseHeader('Content-Disposition');
                if (contentDisposition) {
                    const contentDispositionItems = contentDisposition.split(';');
                    if (contentDispositionItems) {
                        for (let i = 0; i < contentDispositionItems.length; i++) {
                            const currentItem = contentDispositionItems[i];
                            if (currentItem.includes('filename=')) {
                                const n = currentItem.indexOf('filename=') + 9;
                                fileName = contentDispositionItems[i].substring(
                                    n + 1,
                                    contentDispositionItems[i].length - 1
                                );
                                break;
                            }
                        }
                    }
                }
                const a = document.createElement('a');
                a.href = window.URL.createObjectURL(blob);
                a.download = fileName;
                a.dispatchEvent(new MouseEvent('click'));
            }

            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status >= 400) {
                const validation = this.state.validation;
                validation.model = [DisplayMessages.ReportGenerateError];
                this.setState({ pendingResponse: false, validation: validation });
            }
        };

        xhr.send(JSON.stringify(this.state.reportParameters));
    };

    handleExportError = () => {
        const validationCopy = cloneDeep(this.state.validation);
        validationCopy.model = [DisplayMessages.UnexpectedError];
        this.setState({ pendingResponse: false, validation: validationCopy });
    };

    validate = () => {
        const validation = {} as IValidation;

        const { reportTypeDetail, reportParameters } = this.state;
        const options = reportTypeDetail.availableReportOptions;
        const date = reportParameters.date;
        const { auditFields = [], displayFields = [] } = reportParameters;

        const validateDateRange = (): boolean => {
            if (!date || isEmpty(date)) {
                validation.dateRange = ['A date range is required'];
                return true;
            }

            if (!date.startDate) {
                validation.dateRange = ['A start date is required'];
                return true;
            }

            if (!date.endDate) {
                validation.dateRange = ['An end date is required'];
                return true;
            }

            if (date.startDate > date.endDate) {
                validation.dateRange = ['Start date must be before end date'];
                return true;
            }

            return false;
        };

        const validateDisplayFields = (): boolean => {
            if (displayFields.length === 0) {
                validation.displayFields = ['At least 1 Display Field is required'];
                return true;
            }

            return false;
        };

        const validateAuditFields = (): boolean => {
            if (auditFields.length === 0) {
                validation.auditFields = ['At least 1 Audit Field is required'];
                return true;
            }

            return false;
        };

        let hasError = false;

        if (options?.includeDateRange) {
            hasError = validateDateRange() || hasError;
        }

        if (options?.includeDisplayFields) {
            hasError = validateDisplayFields() || hasError;
        }

        if (options?.includeAuditFields) {
            hasError = validateAuditFields() || hasError;
        }

        this.setState({ validation });
        return !hasError;
    };

    render() {
        const { availableReportOptions, displayName, viewInUI, viewTemplateName } =
            this.state.reportTypeDetail;
        const { includeDateRange, includeAuditFields, includeDisplayFields } =
            availableReportOptions || {};

        const defaultShowExport = !viewInUI;
        const showExportButton = !!this.props.typeId;
        const openRunReportModal = defaultShowExport && !includeDateRange;
        const hasMoreThanOneFilter =
            [includeDateRange, includeAuditFields, includeDisplayFields].filter(Boolean).length >=
            2;

        const labelColumnClassNames = classNames('col-xs-4 col-sm-3 col-lg-2', {
            'col-xl-1': !hasMoreThanOneFilter,
        });

        const headerComponent = (
            <>
                <Stack
                    direction="horizontal"
                    style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    <h1>{this.state.reportTypeDetail.displayName}</h1>
                    <Stack
                        direction="horizontal"
                        style={{
                            alignItems: 'center',
                        }}
                        gap={2}
                    >
                        <CurrentFilters
                            availableReportOptions={
                                this.state.reportTypeDetail.availableReportOptions
                            }
                            reportParameters={this.state.reportParameters}
                        />
                        <div>
                            {showExportButton && this.state.reportTypeDetail.viewInUI && (
                                <ExportFileButton
                                    buttonText="PDF"
                                    cssClasses="btn btn-no-bg text-gray"
                                    defaultOpenModal={defaultShowExport}
                                    reportParameters={this.state.reportParameters}
                                    reportType={{
                                        id: this.props.typeId!,
                                        availableReportOptions:
                                            this.state.reportTypeDetail.availableReportOptions,
                                    }}
                                    url={'/' + ApiRoutes.ExportReport}
                                />
                            )}

                            {showExportButton && !this.state.reportTypeDetail.viewInUI && (
                                <ExportFileButton
                                    buttonText="Run Report"
                                    cssClasses="btn btn-orange"
                                    defaultOpenModal={openRunReportModal}
                                    hideExportButtonWhenModalOpen
                                    hideIcon
                                    modalTitle="Run Report"
                                    reportParameters={this.state.reportParameters}
                                    reportType={{
                                        id: this.props.typeId!,
                                        availableReportOptions:
                                            this.state.reportTypeDetail.availableReportOptions,
                                    }}
                                    url={'/' + ApiRoutes.ExportReport}
                                    validateFn={this.validate}
                                />
                            )}

                            {this.state.reportTypeDetail.allowComments &&
                                (this.state.addEditComment ? (
                                    <Button
                                        variant="default"
                                        onClick={() => this.setState({ addEditComment: false })}
                                    >
                                        Hide Comments
                                    </Button>
                                ) : (
                                    <Button
                                        variant="default"
                                        onClick={() => this.setState({ addEditComment: true })}
                                    >
                                        Comments
                                    </Button>
                                ))}

                            {showExportButton && this.state.reportTypeDetail.viewInUI && (
                                <Button variant="primary" onClick={this.refreshReport}>
                                    Run Report
                                </Button>
                            )}
                        </div>
                    </Stack>
                </Stack>
                {this.state.addEditComment && (
                    <div>
                        <label className="control-label mb-2" htmlFor="comments">
                            Comments
                        </label>
                        <textarea
                            className="form-control"
                            value={this.state.reportParameters.commentText}
                            onChange={this.handleCommentChange}
                            rows={2}
                        />
                    </div>
                )}
            </>
        );

        return (
            <PageWithAffixedHeader headerComponent={headerComponent}>
                <DocumentTitle title={displayName} />

                <div>
                    <span className="text-danger">{this.state.validation.model}</span>
                </div>

                <Stack className="mt-2" gap={2}>
                    {includeDateRange && (
                        <div className="row" style={{ alignItems: 'center' }}>
                            <div className={labelColumnClassNames}>
                                <span className="no-wrap">Date Range</span>
                            </div>
                            <div className="col-xs-8 col-sm-9 col-lg-10">
                                <Stack direction="horizontal" gap={3}>
                                    <DateRangePicker
                                        onStartDateChange={(e) =>
                                            this.handleChangeDate(e, DateTypesEnum.StartDate)
                                        }
                                        onEndDateChange={(e) =>
                                            this.handleChangeDate(e, DateTypesEnum.EndDate)
                                        }
                                        startDate={
                                            this.state.reportParameters.date?.startDate
                                                ? Common.dateFormat(
                                                      this.state.reportParameters.date.startDate
                                                  )
                                                : ''
                                        }
                                        endDate={
                                            this.state.reportParameters.date?.endDate
                                                ? Common.dateFormat(
                                                      this.state.reportParameters.date.endDate
                                                  )
                                                : ''
                                        }
                                        startInputFieldName="startDate"
                                        endInputFieldName="endDate"
                                    />
                                    {this.state.validation.dateRange && (
                                        <span className="text-danger">
                                            {this.state.validation.dateRange}
                                        </span>
                                    )}
                                </Stack>
                            </div>
                        </div>
                    )}

                    {includeAuditFields && (
                        <FieldFilter
                            defaultFields={this.props.defaultAuditFieldSettings}
                            label="Audit Fields"
                            labelClassNames={labelColumnClassNames}
                            onChange={this.handleAuditFieldsChange}
                            errors={this.state.validation.auditFields}
                        />
                    )}

                    {includeDisplayFields && (
                        <FieldFilter
                            defaultFields={QueryToolDefaultColumnList}
                            label="Display Fields"
                            labelClassNames={labelColumnClassNames}
                            onChange={this.handleDisplayFieldsChange}
                            errors={this.state.validation.displayFields}
                        />
                    )}
                </Stack>

                {this.state.reportTypeDetail.viewInUI &&
                    (this.state.renderReportViewer || !availableReportOptions) && (
                        <Html5ReportViewer
                            scale={1.3}
                            addSpaceForCommentOnTop={this.state.addEditComment}
                            reportName={viewTemplateName!}
                            reportParameters={this.state.reportParameters}
                            refreshReport={this.state.refreshReport}
                        />
                    )}

                <Modal
                    centered
                    onHide={() => {
                        this.setState({ showSettingsModal: false });
                    }}
                    backdrop={false}
                >
                    <Modal.Header>
                        <Modal.Title>Report Settings</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div>
                            <span className="text-danger">{this.state.validation.model}</span>
                        </div>
                        {this.state.reportTypeDetail.availableReportOptions ? (
                            <>
                                {this.state.reportTypeDetail.availableReportOptions
                                    .includeDateRange && (
                                    <div className="row mb-3">
                                        <div className="col-sm-1">
                                            <label className="control-label" htmlFor="dateRange">
                                                Date
                                            </label>
                                        </div>
                                        <div className="col-sm-11">
                                            <input
                                                type="date"
                                                name="startDate"
                                                className={
                                                    'form-control d-inline-block horizontal-margin' +
                                                    (this.state.reportParameters.date &&
                                                    this.state.reportParameters.date.startDate &&
                                                    Common.isValidDate(
                                                        this.state.reportParameters.date.startDate
                                                    )
                                                        ? ''
                                                        : ' unselectClass')
                                                }
                                                value={
                                                    this.state.reportParameters.date &&
                                                    this.state.reportParameters.date.startDate
                                                        ? Common.dateFormat(
                                                              this.state.reportParameters.date
                                                                  .startDate
                                                          )
                                                        : ''
                                                }
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    this.handleChangeDate(
                                                        e,
                                                        DateTypesEnum.StartDate
                                                    );
                                                }}
                                            />
                                            <span className="horizontal-margin">to</span>
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={
                                                    this.state.reportParameters.date &&
                                                    this.state.reportParameters.date.endDate
                                                        ? this.state.reportParameters.date.endDate
                                                        : ''
                                                }
                                                className={
                                                    'form-control d-inline-block horizontal-margin' +
                                                    (this.state.reportParameters.date &&
                                                    this.state.reportParameters.date.endDate &&
                                                    Common.isValidDate(
                                                        this.state.reportParameters.date.endDate
                                                    )
                                                        ? ''
                                                        : ' unselectClass')
                                                }
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    this.handleChangeDate(e, DateTypesEnum.EndDate);
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {this.state.reportTypeDetail.availableReportOptions
                                    .includePastProjected && (
                                    <div className="mt-2">
                                        <input
                                            name="chkIncludePastProjected"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={
                                                this.state.reportParameters.options &&
                                                this.state.reportParameters.options
                                                    .includePastProjected
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(e);
                                            }}
                                        />
                                        <span>&nbsp;&nbsp;Include Past Projected</span>
                                    </div>
                                )}
                                {this.state.reportTypeDetail.availableReportOptions
                                    .includeNotes && (
                                    <div className="mt-2">
                                        <input
                                            name="chkIncludeNotes"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={
                                                this.state.reportParameters.options?.includeNotes
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(e);
                                            }}
                                        />
                                        <span>&nbsp;&nbsp;Include Significant Changes</span>
                                    </div>
                                )}
                                {this.state.reportTypeDetail.availableReportOptions
                                    .includeTasks && (
                                    <div className="mt-2">
                                        <input
                                            name="chkIncludeTasks"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={
                                                this.state.reportParameters.options?.includeTasks
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(e);
                                            }}
                                        />
                                        <span>&nbsp;&nbsp;Include Tactics</span>
                                    </div>
                                )}
                            </>
                        ) : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Stack
                            direction="horizontal"
                            style={{ width: '100%', justifyContent: 'space-between' }}
                        >
                            <div>
                                <input
                                    name="chkSaveReport"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={this.state.reportParameters.saveReport ? true : false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        this.handleCheckedChange(e);
                                    }}
                                />
                                <span>&nbsp;&nbsp;Save Report</span>
                            </div>

                            <div>
                                <button
                                    className="btn btn-default"
                                    onClick={this.cancelSettingsModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-orange"
                                    onClick={
                                        this.state.reportTypeDetail.viewInUI
                                            ? this.refreshReport
                                            : this.generateAndDownloadReport
                                    }
                                >
                                    Run
                                </button>
                            </div>
                        </Stack>
                    </Modal.Footer>
                </Modal>
            </PageWithAffixedHeader>
        );
    }
}

interface ICurrentFiltersProps {
    availableReportOptions?: any;
    reportParameters: IReportParametersModel;
}

function CurrentFilters(props: ICurrentFiltersProps) {
    return (
        <div>
            {props.availableReportOptions?.includePastProjected && (
                <span style={{ fontSize: '12px' }}>(Include Past Projected Settlements)</span>
            )}
        </div>
    );
}
